// RetrogradeRadar, personalized retrograde period tracker
// Route: /retrogrado

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, ChevronDown, ChevronUp, Lock } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import { useSystemUsageTracker } from '../hooks/useSystemUsageTracker';
import { safeInvoke } from '../lib/safeInvoke';
import { TextSkeleton } from '../components/shared/TextSkeleton';
import { EmptyState } from '../components/ui/EmptyState';

// ─────────────────────────────────────────
// Types
// ─────────────────────────────────────────

interface RetrogradeEvent {
  id:            string;
  planet:        string;
  starts_at:     string;
  ends_at:       string;
  start_sign:    string;
  end_sign:      string;
  shadow_starts: string | null;
  shadow_ends:   string | null;
  year:          number;
}

interface RetrogradeAnalysis {
  personal_impact: string;
  affected_areas:  string[];
  advice:          string;
  opportunities:   string;
}

// ─────────────────────────────────────────
// Constants
// ─────────────────────────────────────────

const PLANET_META: Record<string, { emoji: string; color: string; themes: string }> = {
  'Mercúrio': { emoji: '☿',  color: '#60a5fa', themes: 'comunicação, contratos, viagens, tecnologia' },
  'Vênus':    { emoji: '♀',  color: '#f472b6', themes: 'amor, dinheiro, valores, beleza' },
  'Marte':    { emoji: '♂',  color: '#f97316', themes: 'ação, projetos, conflitos, energia' },
  'Júpiter':  { emoji: '♃',  color: '#fbbf24', themes: 'expansão, fé, oportunidades, crescimento' },
  'Saturno':  { emoji: '♄',  color: '#94a3b8', themes: 'estrutura, responsabilidade, limites, carreira' },
  'Urano':    { emoji: '♅',  color: '#a78bfa', themes: 'inovação, liberdade, rupturas, tecnologia' },
  'Netuno':   { emoji: '♆',  color: '#38bdf8', themes: 'intuição, ilusão, espiritualidade, sonhos' },
  'Plutão':   { emoji: '♇',  color: '#c084fc', themes: 'transformação, poder, sombra, regeneração' },
};

// ─────────────────────────────────────────
// Static fallback, 2026-2027 retrograde calendar
// ─────────────────────────────────────────

const STATIC_RETROGRADE_EVENTS: RetrogradeEvent[] = [
  // ── 2026 ──
  { id: 'merc-rx-2026-may', planet: 'Mercúrio', starts_at: '2026-05-09', ends_at: '2026-06-02', start_sign: 'Gêmeos', end_sign: 'Touro',    shadow_starts: '2026-04-25', shadow_ends: '2026-06-16', year: 2026 },
  { id: 'plut-rx-2026',     planet: 'Plutão',   starts_at: '2026-05-14', ends_at: '2026-10-14', start_sign: 'Aquário', end_sign: 'Capricórnio', shadow_starts: null,         shadow_ends: null,         year: 2026 },
  { id: 'nept-rx-2026',     planet: 'Netuno',   starts_at: '2026-07-04', ends_at: '2026-12-10', start_sign: 'Áries',  end_sign: 'Peixes',    shadow_starts: null,         shadow_ends: null,         year: 2026 },
  { id: 'sat-rx-2026',      planet: 'Saturno',  starts_at: '2026-07-12', ends_at: '2026-11-27', start_sign: 'Áries',  end_sign: 'Peixes',    shadow_starts: '2026-05-27', shadow_ends: '2027-01-08', year: 2026 },
  { id: 'merc-rx-2026-sep', planet: 'Mercúrio', starts_at: '2026-09-08', ends_at: '2026-10-02', start_sign: 'Libra',  end_sign: 'Virgem',    shadow_starts: '2026-08-25', shadow_ends: '2026-10-17', year: 2026 },
  { id: 'uran-rx-2026',     planet: 'Urano',    starts_at: '2026-09-06', ends_at: '2027-02-04', start_sign: 'Gêmeos', end_sign: 'Touro',     shadow_starts: null,         shadow_ends: null,         year: 2026 },
  { id: 'mars-rx-2026',     planet: 'Marte',    starts_at: '2026-12-10', ends_at: '2027-02-23', start_sign: 'Leão',   end_sign: 'Câncer',    shadow_starts: '2026-10-23', shadow_ends: '2027-04-05', year: 2026 },
  { id: 'jup-rx-2026',      planet: 'Júpiter',  starts_at: '2026-12-08', ends_at: '2027-04-08', start_sign: 'Escorpião', end_sign: 'Libra',  shadow_starts: null,         shadow_ends: null,         year: 2026 },
  { id: 'merc-rx-2026-dec', planet: 'Mercúrio', starts_at: '2026-12-26', ends_at: '2027-01-16', start_sign: 'Capricórnio', end_sign: 'Sagitário', shadow_starts: '2026-12-11', shadow_ends: '2027-02-01', year: 2026 },
  // ── 2027 ──
  { id: 'merc-rx-2027-apr', planet: 'Mercúrio', starts_at: '2027-04-09', ends_at: '2027-05-03', start_sign: 'Touro',  end_sign: 'Áries',     shadow_starts: '2027-03-25', shadow_ends: '2027-05-18', year: 2027 },
  { id: 'plut-rx-2027',     planet: 'Plutão',   starts_at: '2027-05-19', ends_at: '2027-10-19', start_sign: 'Aquário', end_sign: 'Aquário',  shadow_starts: null,         shadow_ends: null,         year: 2027 },
  { id: 'merc-rx-2027-aug', planet: 'Mercúrio', starts_at: '2027-08-10', ends_at: '2027-09-03', start_sign: 'Virgem', end_sign: 'Leão',      shadow_starts: '2027-07-26', shadow_ends: '2027-09-19', year: 2027 },
  { id: 'sat-rx-2027',      planet: 'Saturno',  starts_at: '2027-08-05', ends_at: '2027-12-22', start_sign: 'Áries',  end_sign: 'Peixes',    shadow_starts: null,         shadow_ends: null,         year: 2027 },
  { id: 'nept-rx-2027',     planet: 'Netuno',   starts_at: '2027-07-09', ends_at: '2027-12-15', start_sign: 'Áries',  end_sign: 'Peixes',    shadow_starts: null,         shadow_ends: null,         year: 2027 },
  { id: 'merc-rx-2027-dec', planet: 'Mercúrio', starts_at: '2027-12-01', ends_at: '2027-12-21', start_sign: 'Sagitário', end_sign: 'Sagitário', shadow_starts: '2027-11-16', shadow_ends: '2028-01-06', year: 2027 },
];

// ─────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────

function formatDateBR(d: string): string {
  const [y, m, day] = d.split('-');
  const months = ['jan','fev','mar','abr','mai','jun','jul','ago','set','out','nov','dez'];
  return `${parseInt(day)} ${months[parseInt(m) - 1]} ${y}`;
}

function isActive(event: RetrogradeEvent, today: string): boolean {
  return event.starts_at <= today && event.ends_at >= today;
}

function isUpcoming(event: RetrogradeEvent, today: string): boolean {
  return event.starts_at > today;
}

function daysUntil(dateStr: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(dateStr + 'T00:00:00');
  return Math.ceil((target.getTime() - today.getTime()) / 86400000);
}

function daysLeft(dateStr: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const end = new Date(dateStr + 'T00:00:00');
  return Math.max(0, Math.ceil((end.getTime() - today.getTime()) / 86400000));
}

// ─────────────────────────────────────────
// Status banner
// ─────────────────────────────────────────

function StatusBanner({ events, today }: { events: RetrogradeEvent[]; today: string }) {
  const active = events.filter(e => isActive(e, today));
  const meta   = PLANET_META;

  if (active.length === 0) {
    return (
      <div style={{
        padding: '1rem 1.25rem',
        borderRadius: 'var(--radius-md)',
        background: 'rgba(74,222,128,0.08)',
        border: '1px solid rgba(74,222,128,0.25)',
        color: '#4ade80',
        display: 'flex', alignItems: 'center', gap: '0.5rem',
        marginBottom: '1.5rem',
        fontSize: 'var(--text-label)', fontWeight: 600,
      }}>
        ✦ Nenhum planeta retrógrado no momento
      </div>
    );
  }

  return (
    <div style={{ marginBottom: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      {active.map(e => {
        const m = meta[e.planet] ?? { emoji: '↩', color: '#fbbf24', themes: '' };
        return (
          <div key={e.id} style={{
            padding: '0.9rem 1.25rem',
            borderRadius: 'var(--radius-md)',
            background: m.color + '11',
            border: `1px solid ${m.color}33`,
            color: m.color,
            fontSize: 'var(--text-label)', fontWeight: 600,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <span>{m.emoji} {e.planet} Retrógrado em {e.start_sign}</span>
            <span style={{ fontWeight: 400, fontSize: 'var(--text-caption)' }}>
              termina em {daysLeft(e.ends_at)}d
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────
// Retrograde card
// ─────────────────────────────────────────

function isWithin3Months(event: RetrogradeEvent, today: string): boolean {
  const cutoff = new Date(today);
  cutoff.setDate(cutoff.getDate() + 90);
  const cutoffStr = cutoff.toISOString().slice(0, 10);
  return event.starts_at <= cutoffStr && event.ends_at >= today;
}

function RetrogradeCard({
  event,
  today,
  isPremium,
  onAnalyze,
  analysis,
  generating,
}: {
  event:      RetrogradeEvent;
  today:      string;
  isPremium:  boolean;
  onAnalyze:  (id: string) => void;
  analysis:   RetrogradeAnalysis | null;
  generating: boolean;
}) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const meta       = PLANET_META[event.planet] ?? { emoji: '↩', color: '#fbbf24', themes: '' };
  const active     = isActive(event, today);
  const upcoming   = isUpcoming(event, today);
  const past       = !active && !upcoming;
  const canAnalyze = isWithin3Months(event, today);

  const statusLabel = active ? 'ATIVO' : upcoming ? `em ${daysUntil(event.starts_at)}d` : 'encerrado';
  const statusColor = active ? '#4ade80' : upcoming ? meta.color : '#6b7280';

  return (
    <div style={{
      borderRadius: 'var(--radius-lg)',
      border: `1px solid ${active ? meta.color + '44' : 'var(--border-subtle)'}`,
      background: active ? meta.color + '08' : 'var(--surface-card)',
      marginBottom: '0.75rem',
      overflow: 'hidden',
      opacity: past ? 0.7 : 1,
    }}>
      {/* Header */}
      <button
        style={{
          width: '100%', display: 'flex', alignItems: 'center',
          gap: '0.75rem', padding: '1rem 1.25rem',
          background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left',
        }}
        onClick={() => setExpanded(v => !v)}
      >
        <span style={{ fontSize: '1.5rem' }}>{meta.emoji}</span>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.2rem' }}>
            <span style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 'var(--text-label)' }}>
              {event.planet} Retrógrado
            </span>
            <span style={{
              fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.05em',
              padding: '0.15rem 0.5rem', borderRadius: '999px',
              background: statusColor + '22', color: statusColor,
            }}>{statusLabel}</span>
          </div>
          <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>
            {formatDateBR(event.starts_at)} → {formatDateBR(event.ends_at)} · {event.start_sign}
            {event.end_sign !== event.start_sign ? ` → ${event.end_sign}` : ''}
          </span>
        </div>
        {expanded ? <ChevronUp size={16} color="var(--text-muted)" /> : <ChevronDown size={16} color="var(--text-muted)" />}
      </button>

      {expanded && (
        <div style={{ padding: '0 1.25rem 1.25rem' }}>
          {/* Shadow periods */}
          {event.shadow_starts && (
            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
              🌑 Sombra: {formatDateBR(event.shadow_starts)}
              {event.shadow_ends ? `, ${formatDateBR(event.shadow_ends)}` : ''}
            </p>
          )}

          {/* Theme hint */}
          <p style={{ fontSize: 'var(--text-caption)', color: meta.color, marginBottom: '1rem' }}>
            Áreas: {meta.themes}
          </p>

          {/* Personal analysis */}
          {!isPremium && (
            <div style={{ position: 'relative' }}>
              <div style={{ filter: 'blur(4px)', pointerEvents: 'none', userSelect: 'none', opacity: 0.6 }}>
                <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>
                  Sua análise pessoal: {event.planet} no seu mapa natal ativa áreas de comunicação
                  e requer atenção especial a contratos e decisões importantes durante este período.
                  Revise projetos antigos antes de iniciar novos.
                </p>
              </div>
              <div style={{
                position: 'absolute', inset: 0,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                gap: '0.5rem',
              }}>
                <Lock size={18} color="var(--color-gold)" />
                <button className="btn btn-primary btn-sm" onClick={() => navigate('/pricing')}>
                  Ver análise pessoal
                </button>
              </div>
            </div>
          )}

          {isPremium && !analysis && !generating && canAnalyze && (
            <button
              className="btn btn-primary btn-sm"
              onClick={() => onAnalyze(event.id)}
              style={{ gap: '0.4rem' }}
            >
              <Sparkles size={14} />
              Gerar análise pessoal
            </button>
          )}

          {isPremium && !analysis && !generating && !canAnalyze && (
            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', fontStyle: 'italic' }}>
              Análise disponível quando o período estiver nos próximos 3 meses.
            </p>
          )}

          {isPremium && generating && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-muted)', fontSize: 'var(--text-caption)' }}>
              <Sparkles size={14} className="animate-pulse" />
              Gerando sua análise...
            </div>
          )}

          {isPremium && analysis && !generating && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <h4 style={{ fontSize: 'var(--text-label)', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.4rem' }}>
                  Impacto pessoal
                </h4>
                <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                  {analysis.personal_impact}
                </p>
              </div>

              {analysis.affected_areas?.length > 0 && (
                <div>
                  <h4 style={{ fontSize: 'var(--text-label)', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.4rem' }}>
                    Áreas afetadas
                  </h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                    {analysis.affected_areas.map((area, i) => (
                      <span key={i} style={{
                        padding: '0.2rem 0.6rem',
                        borderRadius: '999px',
                        background: meta.color + '22',
                        color: meta.color,
                        fontSize: 'var(--text-caption)',
                      }}>{area}</span>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <h4 style={{ fontSize: 'var(--text-label)', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.4rem' }}>
                  Conselho
                </h4>
                <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                  {analysis.advice}
                </p>
              </div>

              <div>
                <h4 style={{ fontSize: 'var(--text-label)', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.4rem' }}>
                  Oportunidades
                </h4>
                <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                  {analysis.opportunities}
                </p>
              </div>

              <button
                className="btn btn-secondary btn-sm"
                onClick={() => onAnalyze(event.id)}
                style={{ alignSelf: 'flex-start' }}
              >
                Regenerar
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────
// Main page
// ─────────────────────────────────────────

export function RetrogradeRadar() {
  useSystemUsageTracker('retrogrados');
  const { profile } = useAuth();
  const navigate    = useNavigate();

  const [events,      setEvents]     = useState<RetrogradeEvent[]>([]);
  const [analyses,    setAnalyses]   = useState<Record<string, RetrogradeAnalysis>>({});
  const [generating,  setGenerating] = useState<string | null>(null); // retrograde_id being generated
  const [loading,     setLoading]    = useState(true);
  const [error,       setError]      = useState<string | null>(null);
  const [filter,      setFilter]     = useState<'all' | 'active' | 'upcoming'>('all');

  const today     = new Date().toISOString().slice(0, 10);
  const isPremium = profile?.subscription_tier === 'monthly' || profile?.subscription_tier === 'annual' || profile?.unlimited_access === true;

  // Load events, fall back to static calendar if DB table is empty
  useEffect(() => {
    const yearFrom = new Date().getFullYear();
    supabase
      .from('retrograde_events')
      .select('*')
      .gte('year', yearFrom)
      .order('starts_at', { ascending: true })
      .then(({ data }) => {
        if (data && data.length > 0) {
          setEvents(data as RetrogradeEvent[]);
        } else {
          const today = new Date().toISOString().slice(0, 10);
          setEvents(STATIC_RETROGRADE_EVENTS.filter(e => e.ends_at >= today));
        }
        setLoading(false);
      });
  }, []);

  // Load existing analyses for this user, only for DB events (UUID IDs, not static text IDs)
  const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  useEffect(() => {
    if (!profile?.id || events.length === 0) return;
    const dbEventIds = events.filter(e => UUID_RE.test(e.id)).map(e => e.id);
    if (dbEventIds.length === 0) return; // all static events, skip DB query
    supabase
      .from('user_retrograde_analyses')
      .select('retrograde_id, personal_impact, affected_areas, advice, opportunities')
      .eq('user_id', profile.id)
      .in('retrograde_id', dbEventIds)
      .then(({ data }) => {
        if (!data) return;
        const map: Record<string, RetrogradeAnalysis> = {};
        for (const row of data) {
          map[row.retrograde_id] = {
            personal_impact: row.personal_impact,
            affected_areas:  row.affected_areas ?? [],
            advice:          row.advice,
            opportunities:   row.opportunities,
          };
        }
        setAnalyses(map);
      });
  }, [profile?.id, events]);

  const handleAnalyze = useCallback(async (retrogradeId: string) => {
    if (!profile?.birth_date) { navigate('/birth-chart'); return; }
    setGenerating(retrogradeId);
    setError(null);

    const result = await safeInvoke<{ analysis: RetrogradeAnalysis }>(
      'retrograde-analysis',
      {
        body:     { retrograde_id: retrogradeId, regenerate: !!analyses[retrogradeId] },
        timeout:  45_000,
        fallback: null as unknown as { analysis: RetrogradeAnalysis },
      },
    );

    if (result && !result.fromFallback && result.data?.analysis) {
      setAnalyses(prev => ({ ...prev, [retrogradeId]: result.data.analysis }));
    } else {
      setError('Não foi possível gerar a análise. Verifique seu mapa natal e tente novamente.');
    }
    setGenerating(null);
  }, [profile?.birth_date, analyses, navigate]);

  const filtered = events.filter(e => {
    if (filter === 'active')   return isActive(e, today);
    if (filter === 'upcoming') return isUpcoming(e, today);
    return true;
  });

  const activeCount   = events.filter(e => isActive(e, today)).length;
  const upcomingCount = events.filter(e => isUpcoming(e, today)).length;

  if (!profile?.birth_date) {
    return (
      <div className="page-container">
        <EmptyState
          emoji="↩️"
          title="Mapa natal necessário"
          subtitle="Para ver como os retrógrados afetam seu mapa, primeiro calcule seu Mapa Natal."
          cta="Calcular Mapa Natal"
          ctaAction={() => navigate('/birth-chart')}
        />
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 className="page-title">Radar Retrógrado</h1>
        <p className="page-subtitle">
          Acompanhe as retrogradações e descubra como elas afetam seu mapa natal
        </p>
      </div>

      {/* Current status */}
      {!loading && <StatusBanner events={events} today={today} />}

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem' }}>
        {[
          { key: 'all',      label: `Todos (${events.length})` },
          { key: 'active',   label: `Ativos (${activeCount})` },
          { key: 'upcoming', label: `Próximos (${upcomingCount})` },
        ].map(f => (
          <button
            key={f.key}
            className={filter === f.key ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
            onClick={() => setFilter(f.key as typeof filter)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="error-banner" style={{ marginBottom: '1rem' }}>{error}</div>
      )}

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} style={{ borderRadius: 'var(--radius-lg)', border: '1px solid var(--border-subtle)', padding: '1rem', background: 'var(--surface-card)' }}>
              <TextSkeleton lines={2} />
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '2rem' }}>
          Nenhum retrógrado neste filtro.
        </p>
      ) : (
        <div>
          {filtered.map(event => (
            <RetrogradeCard
              key={event.id}
              event={event}
              today={today}
              isPremium={isPremium}
              onAnalyze={handleAnalyze}
              analysis={analyses[event.id] ?? null}
              generating={generating === event.id}
            />
          ))}
        </div>
      )}

      {!isPremium && (
        <div style={{
          marginTop: '2rem', padding: '1.25rem',
          borderRadius: 'var(--radius-lg)',
          background: 'rgba(245,200,66,0.06)',
          border: '1px solid rgba(245,200,66,0.2)',
          textAlign: 'center',
        }}>
          <Lock size={20} color="var(--color-gold)" style={{ margin: '0 auto 0.5rem' }} />
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', marginBottom: '0.75rem' }}>
            Assinantes têm acesso às análises pessoais de cada retrógrado
          </p>
          <button className="btn btn-primary btn-sm" onClick={() => navigate('/pricing')}>
            Assinar agora
          </button>
        </div>
      )}
    </div>
  );
}
